import * as React from 'react'

export type ManifestProbe = {
  href: string
  ok: boolean
  status?: number
  contentType?: string | null
  data?: any
  error?: string
}

export type InstallabilityDiagnostics = {
  secureContext: boolean
  serviceWorkerSupported: boolean
  swRegistered: boolean
  swController: boolean
  swState: string | null
  swDiag: string | null
  manifests: ManifestProbe[]
  preferredManifestHref: string | null
  manifestStartUrl: string | null
  manifestScope: string | null
  manifestDisplay: string | null
  manifestIconsCount: number | null
}

function pickPreferredManifestHref(probes: ManifestProbe[]): string | null {
  if (!probes.length) return null
  const byHref = (substr: string) => probes.find((p) => p.href.includes(substr))
  return (
    byHref('/manifest.json')?.href ||
    byHref('/manifest.webmanifest')?.href ||
    probes[0]!.href
  )
}

export function useInstallabilityDiagnostics() {
  const [loading, setLoading] = React.useState(true)
  const [diag, setDiag] = React.useState<InstallabilityDiagnostics>(() => ({
    secureContext: typeof window !== 'undefined' ? !!(window as any).isSecureContext : false,
    serviceWorkerSupported: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
    swRegistered: false,
    swController: false,
    swState: null,
    swDiag: typeof window !== 'undefined' && typeof (window as any).__SW_DIAG__ !== 'undefined' ? String((window as any).__SW_DIAG__) : null,
    manifests: [],
    preferredManifestHref: null,
    manifestStartUrl: null,
    manifestScope: null,
    manifestDisplay: null,
    manifestIconsCount: null,
  }))

  const refresh = React.useCallback(async () => {
    setLoading(true)
    try {
      const secureContext = typeof window !== 'undefined' ? !!(window as any).isSecureContext : false
      const serviceWorkerSupported = typeof navigator !== 'undefined' && 'serviceWorker' in navigator

      let swRegistered = false
      let swController = false
      let swState: string | null = null
      let swDiag: string | null = typeof (window as any).__SW_DIAG__ !== 'undefined' ? String((window as any).__SW_DIAG__) : null

      if (serviceWorkerSupported) {
        try {
          const reg = await navigator.serviceWorker.getRegistration()
          swRegistered = !!reg
          const sw = reg && (reg.waiting || reg.installing || reg.active)
          swState = sw ? (sw as any).state || null : null
          swController = !!navigator.serviceWorker.controller
        } catch {
          swRegistered = false
          swController = false
          swState = null
        }
      }

      const links = Array.from(document.querySelectorAll('link[rel="manifest"]')) as HTMLLinkElement[]
      const manifests: ManifestProbe[] = []
      for (const link of links) {
        const href = String(link.href || link.getAttribute('href') || '').trim()
        if (!href) continue
        try {
          const resp = await fetch(href, { cache: 'no-store' })
          const ct = resp.headers.get('content-type')
          if (!resp.ok) {
            manifests.push({ href, ok: false, status: resp.status, contentType: ct })
            continue
          }
          let data: any = null
          try { data = await resp.json() } catch { data = null }
          manifests.push({ href, ok: true, status: resp.status, contentType: ct, data })
        } catch (e: any) {
          manifests.push({ href, ok: false, error: e?.message || String(e) })
        }
      }

      const preferredManifestHref = pickPreferredManifestHref(manifests)
      const preferred = preferredManifestHref ? manifests.find((m) => m.href === preferredManifestHref) : null
      const manifestStartUrl = preferred?.data?.start_url ? String(preferred.data.start_url) : null
      const manifestScope = preferred?.data?.scope ? String(preferred.data.scope) : null
      const manifestDisplay = preferred?.data?.display ? String(preferred.data.display) : null
      const manifestIconsCount = Array.isArray(preferred?.data?.icons) ? preferred!.data.icons.length : null

      setDiag({
        secureContext,
        serviceWorkerSupported,
        swRegistered,
        swController,
        swState,
        swDiag,
        manifests,
        preferredManifestHref,
        manifestStartUrl,
        manifestScope,
        manifestDisplay,
        manifestIconsCount,
      })
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { void refresh() }, [refresh])

  return { loading, diag, refresh }
}

